/**
 * PhotoHoverPreview — attaches "hover to preview" behaviour to every
 * `<a>` inside a container that points to an image URL.
 *
 * Usage:
 *   const containerRef = useRef(null);
 *   usePhotoHoverPreview(containerRef);
 *
 * On hover a small floating card shows the thumbnail; on click the
 * link opens normally (default behaviour is preserved).
 */
import { useEffect, useRef } from "react";

const IMG_EXT_RE = /\.(jpe?g|png|webp|gif|avif|bmp|heic|heif)(\?|#|$)/i;

function isImageUrl(href) {
  if (!href) return false;
  if (IMG_EXT_RE.test(href)) return true;
  // Cloudinary URLs — usually contain /image/upload/ or res.cloudinary.com
  if (/res\.cloudinary\.com|\/image\/upload\//i.test(href)) return true;
  return false;
}

export function usePhotoHoverPreview(containerRef) {
  const popoverRef = useRef(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Create the popover element once and mount into <body> so it can
    // escape overflow:hidden ancestors.
    const pop = document.createElement("div");
    pop.style.cssText =
      "position:fixed;z-index:9999;pointer-events:none;opacity:0;transition:opacity .12s ease;" +
      "background:#fff;border:1px solid #cbd5e1;border-radius:12px;padding:6px;" +
      "box-shadow:0 20px 40px -12px rgba(0,0,0,0.25);max-width:280px;";
    pop.setAttribute("data-testid", "photo-hover-preview");
    document.body.appendChild(pop);
    popoverRef.current = pop;

    const positionPop = (evt) => {
      const rect = pop.getBoundingClientRect();
      const vw = window.innerWidth; const vh = window.innerHeight;
      let x = evt.clientX + 16;
      let y = evt.clientY + 16;
      if (x + rect.width  > vw - 8) x = evt.clientX - rect.width  - 16;
      if (y + rect.height > vh - 8) y = evt.clientY - rect.height - 16;
      pop.style.left = Math.max(8, x) + "px";
      pop.style.top  = Math.max(8, y) + "px";
    };

    const showFor = (anchor, evt) => {
      const href = anchor.getAttribute("href");
      if (!isImageUrl(href)) return;
      const label = anchor.textContent?.trim() || "Poza";
      pop.innerHTML = "";
      const img = document.createElement("img");
      img.src = href;
      img.alt = label;
      img.style.cssText = "display:block;max-width:260px;max-height:200px;border-radius:8px;object-fit:contain;background:#f1f5f9;";
      pop.appendChild(img);
      const cap = document.createElement("div");
      cap.textContent = label;
      cap.style.cssText = "font-size:11px;color:#475569;text-align:center;padding:4px 2px 2px;font-weight:600;";
      pop.appendChild(cap);
      pop.style.opacity = "1";
      positionPop(evt);
    };

    const hide = () => {
      pop.style.opacity = "0";
    };

    // --- Desktop: hover preview -----------------------------------------
    const onOver = (e) => {
      const a = e.target.closest && e.target.closest("a");
      if (!a || !container.contains(a)) return;
      showFor(a, e);
    };
    const onMove = (e) => {
      if (pop.style.opacity === "1") positionPop(e);
    };
    const onOut = (e) => {
      const a = e.target.closest && e.target.closest("a");
      if (!a) return;
      const to = e.relatedTarget;
      if (to && a.contains(to)) return;
      hide();
    };

    // --- Touch devices: tap once = preview, tap again = open ------------
    // Tracks whether we've already showed a preview for this anchor so a
    // second tap navigates like normal. Also dismisses on outside tap.
    let previewedAnchor = null;

    const onTouchStart = (e) => {
      const a = e.target.closest && e.target.closest("a");
      if (a && container.contains(a) && isImageUrl(a.getAttribute("href"))) {
        if (previewedAnchor === a) {
          // Second tap on the same link → allow default (opens image)
          previewedAnchor = null;
          hide();
          return;
        }
        // First tap → prevent navigation, show preview centered near the touch
        e.preventDefault();
        const touch = e.touches[0] || { clientX: window.innerWidth / 2, clientY: window.innerHeight / 3 };
        showFor(a, touch);
        previewedAnchor = a;
      } else {
        // Tap outside any image link → dismiss any open preview
        if (previewedAnchor) {
          previewedAnchor = null;
          hide();
        }
      }
    };

    const onClickCapture = (e) => {
      // On touch devices some browsers still fire click after touchstart —
      // block it too if we're currently previewing this anchor.
      const a = e.target.closest && e.target.closest("a");
      if (a && a === previewedAnchor) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    container.addEventListener("mouseover", onOver);
    container.addEventListener("mousemove", onMove);
    container.addEventListener("mouseout", onOut);
    document.addEventListener("touchstart", onTouchStart, { passive: false });
    container.addEventListener("click", onClickCapture, true);
    return () => {
      container.removeEventListener("mouseover", onOver);
      container.removeEventListener("mousemove", onMove);
      container.removeEventListener("mouseout", onOut);
      document.removeEventListener("touchstart", onTouchStart);
      container.removeEventListener("click", onClickCapture, true);
      if (pop.parentNode) pop.parentNode.removeChild(pop);
    };
  }, [containerRef]);

  // Return nothing — the popover is rendered imperatively into <body>.
  return null;
}
